import { diffDays } from './dates.js';

// Formatação conforme preferências do usuário (idioma) e do lar (moeda e fuso).
// Padrão: português do Brasil, BRL, America/Sao_Paulo.
const settings = { locale: 'pt-BR', currency: 'BRL', timeZone: 'America/Sao_Paulo' };
const cache = new Map();

export function setFormatSettings(next) {
  Object.assign(settings, Object.fromEntries(Object.entries(next).filter(([, v]) => v)));
  cache.clear();
}
export function getFormatSettings() {
  return { ...settings };
}

function fmt(key, factory) {
  let f = cache.get(key);
  if (!f) {
    f = factory();
    cache.set(key, f);
  }
  return f;
}

/** Valores monetários chegam como string decimal exata ("1234.56"). */
export function formatMoney(amount, { signed = false } = {}) {
  if (amount === null || amount === undefined || amount === '') return '—';
  const f = fmt(
    `money:${signed}`,
    () =>
      new Intl.NumberFormat(settings.locale, {
        style: 'currency',
        currency: settings.currency,
        signDisplay: signed ? 'exceptZero' : 'auto',
      }),
  );
  // Intl aceita string decimal com precisão exata nos navegadores atuais.
  return f.format(String(amount));
}

export function formatNumber(n, maxDigits = 3) {
  if (n === null || n === undefined || n === '') return '';
  return fmt(
    `num:${maxDigits}`,
    () => new Intl.NumberFormat(settings.locale, { maximumFractionDigits: maxDigits }),
  ).format(String(n));
}

export function formatQuantity(quantity, unit) {
  if (quantity === null || quantity === undefined || quantity === '') return unit ?? '';
  return `${formatNumber(quantity)}${unit ? ` ${unit}` : ''}`;
}

/** Data civil 'AAAA-MM-DD' (sem fuso). */
export function formatDate(d, style = 'medium') {
  if (!d) return '';
  const opts = {
    short: { day: '2-digit', month: '2-digit' },
    medium: { day: 'numeric', month: 'short', year: 'numeric' },
    long: { weekday: 'long', day: 'numeric', month: 'long' },
    weekday: { weekday: 'short', day: 'numeric', month: 'short' },
    numeric: { day: '2-digit', month: '2-digit', year: 'numeric' },
    month: { month: 'long', year: 'numeric' },
  }[style];
  return fmt(
    `date:${style}`,
    () => new Intl.DateTimeFormat(settings.locale, { ...opts, timeZone: 'UTC' }),
  ).format(new Date(`${d.slice(0, 10)}T12:00:00Z`));
}

/** Instante ISO exibido no fuso do lar. */
export function formatDateTime(iso, style = 'medium') {
  if (!iso) return '';
  const opts =
    style === 'time'
      ? { hour: '2-digit', minute: '2-digit' }
      : style === 'short'
        ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
        : { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  return fmt(
    `dt:${style}:${settings.timeZone}`,
    () => new Intl.DateTimeFormat(settings.locale, { ...opts, timeZone: settings.timeZone }),
  ).format(new Date(iso));
}

export function formatTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return fmt(
    'time',
    () => new Intl.DateTimeFormat(settings.locale, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }),
  ).format(new Date(Date.UTC(2000, 0, 1, h, m)));
}

/** "Hoje", "Amanhã", "Ontem", "sáb., 12 de set." */
export function relativeDay(d, today) {
  if (!d) return '';
  const n = diffDays(today, d);
  if (n === 0) return 'Hoje';
  if (n === 1) return 'Amanhã';
  if (n === -1) return 'Ontem';
  if (n > 1 && n < 7) return capitalize(formatDate(d, 'weekday'));
  return formatDate(d, n > -300 && n < 300 ? 'weekday' : 'medium');
}

/** Descrição de prazo com ênfase em atraso. */
export function dueLabel(d, today) {
  if (!d) return null;
  const n = diffDays(today, d);
  if (n < 0) return { text: n === -1 ? 'Atrasada desde ontem' : `Atrasada há ${-n} dias`, tone: 'danger' };
  if (n === 0) return { text: 'Hoje', tone: 'warning' };
  if (n === 1) return { text: 'Amanhã', tone: 'neutral' };
  return { text: relativeDay(d, today), tone: 'neutral' };
}

export function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const rtf = fmt('rtf', () => new Intl.RelativeTimeFormat(settings.locale, { numeric: 'auto' }));
  const mins = Math.round(diffMs / 60000);
  if (Math.abs(mins) < 1) return 'agora';
  if (Math.abs(mins) < 60) return rtf.format(-mins, 'minute');
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(-hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return rtf.format(-days, 'day');
  return formatDateTime(iso, 'short');
}

export function capitalize(s) {
  return s ? s[0].toLocaleUpperCase(settings.locale) + s.slice(1) : s;
}

export function initials(name) {
  return String(name ?? '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('');
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${formatNumber(v.toFixed(i ? 1 : 0), 1)} ${units[i]}`;
}
